import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../components/FeatherIcon'
import { LoadingBlock } from '../components/Spinner'
import { useAgo } from '../dynamicLists/hooks'
import { Modal } from '../dynamicLists/shared'
import type { TrackingLinksApi } from './api'
import { count, fillDays, linkUrl, money, percent, rangeOf, trialUrl, utcDay, type RangeChoice } from './format'
import { RangePicker } from './parts'
import type { DailyPoint, Earning, LinkDetail, LinkRow } from './types'

/**
 * One link: its performance over a range (the list's range to start with), the
 * earnings chart and its traffic cost.
 */
export function LinkDetailView({
  api,
  row,
  earning: initialEarning,
  range: initialRange,
  onClose,
  onChanged,
  onArchive,
}: {
  api: TrackingLinksApi
  row: LinkRow
  earning: Earning
  range: RangeChoice
  onClose: () => void
  onChanged: () => void
  onArchive?: (archived: boolean) => void
}) {
  const { t } = useTranslation('trackingLinks')
  const ago = useAgo()
  const [choice, setChoice] = useState<RangeChoice>(initialRange)
  const [earning, setEarning] = useState<Earning>(initialEarning)
  const [data, setData] = useState<LinkDetail | null>(null)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)

  const range = rangeOf(choice)
  const load = useCallback(async () => {
    setError('')
    try {
      setData(await api.detail(row.account_id, row.kind, row.link_id, range, earning))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
    // range is derived from the choice
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, row.account_id, row.kind, row.link_id, range.from, range.to, earning])

  useEffect(() => {
    void load()
  }, [load])

  const link = data?.link ?? row
  const url = link.kind === 'trial' ? trialUrl(link.profile_username, link.trial_code) : linkUrl(link.profile_username, link.campaign_code)
  const copyUrl = async () => {
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1600)
    } catch {
      window.prompt(t('detail.copyPrompt'), url)
    }
  }

  return (
    <Modal side wide title={link.name} onClose={onClose}>
      <div className="tl-detail">
        <div className="tl-detail-head">
          <span className="dl-muted">
            {link.profile_name}
            {link.profile_username && ` · @${link.profile_username}`}
            {link.created_at && ` · ${t('links.created', { date: link.created_at.slice(0, 10) })}`}
          </span>
          {link.kind === 'trial' && <span className="dl-badge">{t('detail.trialBadge')}</span>}
          {link.is_deleted && <span className="dl-badge dl-badge--quiet">{t('links.deleted')}</span>}
          {link.archived && <span className="dl-badge dl-badge--quiet">{t('archive.badge')}</span>}
          <span className="tl-detail-actions">
            {url && (
              <button type="button" className="dl-link" onClick={() => void copyUrl()} title={url}>
                <FeatherIcon name={copied ? 'check' : 'copy'} size={13} /> {copied ? t('detail.copied') : t('detail.copyLink')}
              </button>
            )}
            {onArchive && (
              <button type="button" className="dl-link" onClick={() => onArchive(!link.archived)} title={t(link.archived ? 'archive.restoreTitle' : 'archive.archiveTitle')}>
                <FeatherIcon name={link.archived ? 'rotate-ccw' : 'archive'} size={13} /> {t(link.archived ? 'archive.restoreShort' : 'archive.archiveShort')}
              </button>
            )}
          </span>
        </div>

        <div className="tl-controls">
          <RangePicker value={choice} onChange={setChoice} />
          <div className="dl-seg" role="group" aria-label={t('earning.label')}>
            {(['net', 'gross'] as const).map((e) => (
              <button key={e} type="button" className={`dl-seg-btn ${earning === e ? 'is-on' : ''}`} aria-pressed={earning === e} onClick={() => setEarning(e)}>
                {t(`earning.${e}`)}
              </button>
            ))}
          </div>
        </div>

        {error && <p className="dl-error">{error}</p>}
        {!data ? (
          !error && <LoadingBlock label={t('loading')} />
        ) : (
          <>
            <Kpis link={data.link} />
            <Notes detail={data} ago={ago} />
            <section className="tl-section" aria-label={t(weekly(data) ? 'detail.chartWeeks' : 'detail.chart')}>
              <h3 className="dl-h3">{t(weekly(data) ? 'detail.chartWeeks' : 'detail.chart')}</h3>
              <EarningsChart points={fillDays(data.daily, data.range.from ?? link.created_at?.slice(0, 10) ?? null, data.range.to ?? utcDay(new Date()))} weekly={weekly(data)} />
            </section>
            <Costs api={api} detail={data} onChanged={() => (onChanged(), void load())} />
          </>
        )}
      </div>
    </Modal>
  )
}

function Kpis({ link }: { link: LinkRow }) {
  const { t } = useTranslation('trackingLinks')
  const signed = (n: number | null) => (n == null ? '' : n >= 0 ? 'tl-good' : 'tl-bad')
  const share = (n: number | null, s: number | null) => (n == null ? '—' : s == null ? count(n) : `${count(n)} (${percent(s)})`)
  const tiles: Array<{ key: string; value: string; note?: string; className?: string }> = [
    { key: 'clicks', value: count(link.clicks), note: link.clicks_partial ? t('kpi.clicksPartial') : undefined },
    ...(link.kind === 'trial'
      ? [{ key: 'claims', value: `${count(link.claims)} / ${link.claims_cap == null ? '∞' : count(link.claims_cap)}` }]
      : []),
    { key: 'fans', value: count(link.fans), note: link.fans_from_onlyfans ? t('kpi.fansFromOnlyFans') : undefined },
    { key: 'conversion', value: percent(link.conversion) },
    { key: 'earnings', value: link.earnings == null ? '—' : money(link.earnings), note: link.earnings == null ? t('kpi.earningsPending') : undefined },
    { key: 'cost', value: money(link.cost) },
    { key: 'profit', value: money(link.profit), className: signed(link.profit) },
    { key: 'roi', value: percent(link.roi), className: signed(link.roi) },
    { key: 'spenders', value: share(link.spenders, link.spenders_share) },
    ...(link.kind === 'trial' ? [{ key: 'paid_after_trial', value: count(link.paid_after_trial) }] : []),
    { key: 'replies', value: share(link.replies, link.replies_share) },
    {
      key: 'fleet_earnings',
      value: money(link.fleet_earnings),
      note: link.fleet_earnings == null ? undefined : t('links.otherProfiles', { amount: money(link.fleet_other ?? 0) }),
    },
    { key: 'cpc', value: money(link.cpc) },
    { key: 'epc', value: money(link.epc) },
    { key: 'cpf', value: money(link.cpf) },
    { key: 'arpf', value: money(link.arpf) },
  ]
  return (
    <div className="tl-kpis">
      {tiles.map((k) => (
        <div key={k.key} className="tl-kpi" title={t(`colHelp.${k.key}`, { since: '' })}>
          <span className="dl-label">{t(`col.${k.key}`)}</span>
          <strong className={`tl-kpi-value ${k.className ?? ''}`}>{k.value}</strong>
          {k.note && <span className={k.key === 'fleet_earnings' ? 'tl-sub' : 'tl-kpi-note'}>{k.note}</span>}
        </div>
      ))}
    </div>
  )
}

function Notes({ detail, ago }: { detail: LinkDetail; ago: (iso: string | null | undefined) => string }) {
  const { t } = useTranslation('trackingLinks')
  const l = detail.link
  const lines: string[] = []
  if (l.kind === 'trial') {
    lines.push(
      t('notes.trial', {
        days: l.trial_days ?? '—',
        cap: l.claims_cap == null ? t('notes.noCap') : count(l.claims_cap),
        end: l.expires_at ? l.expires_at.slice(0, 10) : t('notes.noEnd'),
      }),
    )
    if (l.is_finished) lines.push(t('notes.trialFinished'))
  }
  if (l.archived) lines.push(t('notes.archived'))
  if (l.fans_backlog > 0) lines.push(t('notes.backlog', { count: l.fans_backlog, date: (l.watched_since || '').slice(0, 10) }))
  if (l.claimers_error) lines.push(t('notes.readFailed', { message: l.claimers_error }))
  if (l.claimers_read_at) lines.push(t('notes.updated', { ago: ago(l.claimers_read_at) }))
  else if (detail.reading.requested) lines.push(t('notes.reading'))
  else lines.push(t('notes.notRead'))
  return (
    <ul className="tl-notes">
      {lines.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  )
}

/** Whether fillDays grouped this range by week. */
function weekly(d: LinkDetail): boolean {
  const from = d.range.from ?? d.link.created_at?.slice(0, 10) ?? d.daily[0]?.day
  if (!from) return false
  const to = d.range.to ?? utcDay(new Date())
  return (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000 + 1 > 120
}

/** Earnings per UTC day (or week) as bars; new fans as dots above them. */
export function EarningsChart({ points, weekly = false }: { points: DailyPoint[]; weekly?: boolean }) {
  const { t } = useTranslation('trackingLinks')
  if (!points.length || points.every((p) => !p.earnings && !p.fans)) return <p className="dl-muted">{t('detail.noDays')}</p>
  const max = Math.max(1, ...points.map((p) => p.earnings))
  const total = points.reduce((s, p) => s + p.earnings, 0)
  return (
    <div className="tl-chart" role="img" aria-label={t(weekly ? 'detail.chartLabelWeeks' : 'detail.chartLabel', { total: money(total), days: points.length })}>
      <div className="tl-bars">
        {points.map((p) => (
          <div key={p.day} className="tl-bar-col" title={`${weekly ? t('detail.weekOf', { day: p.day }) : p.day}: ${money(p.earnings)}${p.fans ? ` · ${t('detail.newFans', { count: p.fans })}` : ''}`}>
            {p.fans > 0 && <span className="tl-fan-dot" />}
            <span className={`tl-bar ${p.earnings ? '' : 'is-zero'}`} style={{ height: `${p.earnings ? Math.max(3, (p.earnings / max) * 100) : 1}%` }} />
          </div>
        ))}
      </div>
      <div className="tl-chart-axis">
        <span>{points[0]?.day}</span>
        <span>{t('detail.peak', { amount: money(max) })}</span>
        <span>{points[points.length - 1]?.day}</span>
      </div>
    </div>
  )
}

function Costs({ api, detail, onChanged }: { api: TrackingLinksApi; detail: LinkDetail; onChanged: () => void }) {
  const { t } = useTranslation('trackingLinks')
  const [amount, setAmount] = useState('')
  const [paidOn, setPaidOn] = useState(utcDay(new Date()))
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const n = Number(amount)
  const valid = amount.trim() !== '' && Number.isFinite(n) && n >= 0 && Boolean(paidOn)

  const add = async () => {
    setBusy(true)
    setError('')
    try {
      await api.addCost(detail.link.account_id, detail.link.kind, detail.link.link_id, n, paidOn, note.trim() || null)
      setAmount('')
      setNote('')
      onChanged()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }
  const remove = async (id: string) => {
    setError('')
    try {
      await api.deleteCost(id)
      onChanged()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <section className="tl-section" aria-label={t('cost.title')}>
      <h3 className="dl-h3">{t('cost.title')}</h3>
      <p className="dl-hint">{t('cost.hint')}</p>
      {detail.costs.length > 0 && (
        <ul className="tl-costs">
          {detail.costs.map((c) => (
            <li key={c.id}>
              <span className="tl-cost-date">{c.paid_on}</span>
              <strong>{money(c.amount)}</strong>
              {c.note && <span className="dl-muted dl-ellipsis">{c.note}</span>}
              <button type="button" className="dl-iconbtn" onClick={() => void remove(c.id)} aria-label={t('cost.remove', { amount: money(c.amount), date: c.paid_on })}>
                <FeatherIcon name="trash-2" size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="tl-cost-form">
        <span className="dl-num-box tl-money">
          <span className="dl-muted">$</span>
          <input type="number" min={0} step="any" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={t('cost.amount')} aria-label={t('cost.amount')} />
        </span>
        <input type="date" className="dl-input" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} aria-label={t('cost.paidOn')} />
        <input className="dl-input tl-cost-note" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} placeholder={t('cost.note')} aria-label={t('cost.note')} />
        <button type="button" className="dl-btn" disabled={!valid || busy} onClick={() => void add()}>
          <FeatherIcon name="plus" size={14} /> {t('cost.add')}
        </button>
      </div>
      {error && <p className="dl-error">{error}</p>}
    </section>
  )
}
